import React, { Component, createRef } from "react";

// CSS
import "./Lessons.scss";

// Helper
import { setCookie, checkCookie } from "../../Helper/Cookies";
import { apiFetch, getCurrentUser } from "../../Helper/Api";

// Components
import Header from "../../components/Header/Header";
import { Panel, Confirm } from "../../components/UI/Overlays";
import { EmptyState, TableWait, Chip, Tip } from "../../components/UI/Feedback";
import { Toolbar } from "../../components/UI/Controls";
import AddLesson from "./AddLesson";
import EditLesson from "./EditLesson";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";

// Properties
const properties = require("../../properties.json");

class AllLessons extends Component {
  constructor(props) {
    super(props);
    this.toast = createRef(null);
  }

  state = {
    globalFilter: null,

    // Whether the add-a-lesson panel is open. Nothing else about adding a
    // lesson lives here: the panel mounts the very same AddLesson component
    // the /lessons/add route mounts, with its own state and its own submit.
    addOpen: false,

    // The rows are on their way. Until they arrive the table says so, instead
    // of claiming there are none.
    tableLoading: true,

    // The lesson being edited, mounted in its own panel over the list.
    editId: null,

    // The lesson the teacher asked to delete, held while the confirm is on
    // screen. The delete behind it is the same call it always was.
    doomed: null,
    deleting: false,

    // Teacher Identifier
    branchID: getCurrentUser()?.branchId,
    companyID: properties.company_id,

    // Named Months
    namedMonths: [
      "يناير",
      "فبراير",
      "مارس",
      "إبريل",
      "مايو",
      "يونيو",
      "يوليو",
      "أغسطس",
      "سبتمبر",
      "أكتوبر",
      "نوفمبر",
      "ديسمبر",
    ],

    // Lesson Type
    lessonTypes: ["مدفوع", "مجاني"],

    // Data
    data: [],
    stagesById: {},
    materialsById: {},
    periodsById: {},

    // Update Data
    resend: false,
  };

  getLessons = () => {
    // teacherUserId is resolved from the JWT on the server -- no need to send it.
    this.setState({ tableLoading: true });
    apiFetch("/lessons")
      .then((fetchLessons) => {
        this.setState({
          data: fetchLessons,
          tableLoading: false,
        });
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setState({ tableLoading: false });
      });
  };

  getStages = () => {
    apiFetch("/lookup/school-stages/by-teacher", { query: { teacherUserId: getCurrentUser()?.userId } })
      .then((stages) => {
        const stagesById = {};
        stages.forEach((s) => {
          stagesById[s.id] = s.name;
        });
        this.setState({ stagesById });
      })
      .catch((error) => console.error("Error:", error));
  };

  // A row carries ids for its material and its period; these two turn them
  // into the names the teacher knows them by. Same shape as getStages.
  getMaterials = () => {
    const user = getCurrentUser();
    apiFetch("/lookup/materials", { query: { branchId: user?.branchId, teacherUserId: user?.userId } })
      .then((materials) => {
        const materialsById = {};
        materials.forEach((m) => {
          materialsById[m.id] = m.name;
        });
        this.setState({ materialsById });
      })
      .catch((error) => console.error("Error:", error));
  };

  getPeriods = () => {
    apiFetch("/periods")
      .then((periods) => {
        const periodsById = {};
        periods.forEach((x) => {
          periodsById[x.id] = x.name;
        });
        this.setState({ periodsById });
      })
      .catch((error) => console.error("Error:", error));
  };

  // The browser's own confirm box is gone; the question is asked in the
  // product's own confirm card instead (see render). Everything after the
  // teacher says yes -- the call, the toast, the refetch -- is unchanged.
  deleteMedia = (id) => {
    var _this = this;

    this.setState({ deleting: true });

    // Delete Lesson By ID
    apiFetch(`/lessons/${id}`, { method: "DELETE" })
      .then(() => {
        _this.toast.current.show({
          severity: "success",
          summary: "تمت العملية",
          detail: "تم الحذف بنجاح",
        });
        this.setState({ resend: true, doomed: null, deleting: false });
        this.getLessons();
      })
      .catch((error) => {
        console.error("Error:", error);

        this.setState({ doomed: null, deleting: false });

        _this.toast.current.show({
          severity: "error",
          summary: "لم تتم العملية",
          detail: "خطأ في الإتصال",
        });
      });
  };

  componentDidMount() {
    document.title = properties.site_name + " | كل الدروس";

    this.getStages();
    this.getMaterials();
    this.getPeriods();
    this.getLessons();
  }

  // componentDidUpdate(prevProps, prevState, snapshot) {
  //     if (prevState.resend !== this.state.resend) {
  //         this.getLessons();

  //         this.setState({resend: false});
  //     }
  // }

  render() {
    const { tableLoading, doomed, deleting, editId } = this.state;

    // Adding a lesson is something you do on this page now, not a second
    // entry in the menu: the button opens the very same form the /lessons/add
    // route still mounts, in a panel over the list.
    const openAdd = () => this.setState({ addOpen: true });
    const closeAdd = () => {
      this.setState({ addOpen: false });
      // whatever was added while the panel was open belongs in the table
      this.getLessons();
    };

    const closeEdit = () => {
      this.setState({ editId: null });
      this.getLessons();
    };

    const actionBodyTemplate = (rowData) => {
      return (
        <React.Fragment>
          <Tip text="تعديل">
            <Button
              icon="pi pi-pencil"
              className="p-button-rounded p-button-secondary p-ml-2"
              onClick={() => this.setState({ editId: rowData.id })}
            />
          </Tip>
          <Tip text="حذف">
            <Button
              icon="pi pi-trash"
              className="p-button-rounded p-button-danger"
              onClick={() => this.setState({ doomed: rowData })}
            />
          </Tip>
        </React.Fragment>
      );
    };

    // "مدفوع" and "مجاني" are a state, so they are read as one
    const typeBody = (row) => (
      <Chip kind={row.lessonType === 1 ? "ok" : "wait"}>
        {this.state.lessonTypes[row.lessonType]}
      </Chip>
    );

    // the lesson, as the teacher recognises it: its thumbnail, its name, and
    // the material it belongs to under it
    const nameBody = (row) => {
      const thumb = row.thumb || row.image || (row.images && row.images[0]) || null;
      return (
        <span className="ui-row-lead">
          {thumb ? (
            <img className="thumb" src={thumb} alt="" loading="lazy" />
          ) : (
            <span className="thumb is-empty" aria-hidden="true">
              <i className="pi pi-video"></i>
            </span>
          )}
          <span className="tx">
            <b>{row.lessonName}</b>
            <small>{this.state.materialsById[row.materialId] || "—"}</small>
          </span>
        </span>
      );
    };

    // what the lesson actually carries, counted from the row itself
    const contentBody = (row) => {
      const videos =
        (row.videoLinks || []).length +
        (row.youtubeLinks || []).length +
        (row.vimeoLinks || []).length;
      const voices = (row.voices || []).length;
      const images = (row.images || []).length;

      if (!videos && !voices && !images) {
        return <span className="ui-bits is-none">لسه فاضية</span>;
      }

      return (
        <span className="ui-bits">
          {videos ? (
            <span className="bit" title="فيديو">
              <i className="pi pi-video"></i>
              {videos}
            </span>
          ) : null}
          {voices ? (
            <span className="bit" title="صوت">
              <i className="pi pi-volume-up"></i>
              {voices}
            </span>
          ) : null}
          {images ? (
            <span className="bit" title="صور">
              <i className="pi pi-image"></i>
              {images}
            </span>
          ) : null}
        </span>
      );
    };

    return (
      <>
        <Header
          pageName="كل الدروس"
          action={{ label: "إضافة حصة", icon: "pi pi-plus", onClick: openAdd }}
        />

        <Toast ref={this.toast}></Toast>

        {/* The form is not copied here -- the panel mounts AddLesson itself,
            so its fields, its uploads and its submit stay exactly as they are
            on /lessons/add, which still works as its own page. */}
        <Panel
          visible={this.state.addOpen}
          onHide={closeAdd}
          title="إضافة حصة"
          subtitle="املأ بيانات الحصة، وهتظهر في الجدول بعد الحفظ"
        >
          {/* the panel closes itself on a successful save, and the table
              behind it goes and fetches the row that was just added */}
          {this.state.addOpen ? <AddLesson inPanel onSaved={closeAdd} /> : null}
        </Panel>

        {/* editing opens the same form the /lessons/edit/:id route mounts,
            in a panel over the list -- the route still works on its own */}
        <Panel
          visible={!!editId}
          onHide={closeEdit}
          title="تعديل الحصة"
          subtitle="عدّل البيانات واحفظ، والجدول هيتحدّث لوحده"
        >
          {editId ? (
            <EditLesson inPanel lessonId={editId} onSaved={closeEdit} />
          ) : null}
        </Panel>

        <Confirm
          visible={!!doomed}
          onHide={() => this.setState({ doomed: null })}
          name={doomed ? doomed.lessonName : ""}
          note="الحصة هتتشال نهائياً بكل محتواها، ومش هينفع ترجع عنها."
          busy={deleting}
          onConfirm={() => this.deleteMedia(doomed.id)}
        />

        <div className="content">
          <div className="container">
            <div className="card">
              <Card>
                {/* search over the same globalFilter the table always read */}
                <Toolbar
                  value={this.state.globalFilter || ""}
                  onSearch={(v) => this.setState({ globalFilter: v })}
                  placeholder="بحث في الدروس"
                  count={tableLoading ? null : `${this.state.data.length} حصة`}
                />

                <DataTable
                  sortable
                  globalFilter={this.state.globalFilter}
                  className="p-datatable-responsive-demo p-datatable-gridlines p-datatable-striped"
                  value={this.state.data}
                  paginator
                  rows={10}
                  rowsPerPageOptions={[10, 20, 50]}
                  currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                  emptyMessage={
                    tableLoading ? (
                      <TableWait />
                    ) : (
                      <EmptyState
                        title="لا يوجد دروس"
                        hint="ابدأ بإضافة أول حصة لطلابك"
                        icon="pi pi-video"
                        action={{ label: "إضافة حصة", onClick: openAdd }}
                      />
                    )
                  }
                >
                  {/* No per-column filter boxes: the search in the toolbar
                      already looks across the whole table, and the second row
                      of empty inputs under the headers was only taking room. */}
                  <Column
                    sortable
                    field="lessonName"
                    body={nameBody}
                    header="الحصة"
                  ></Column>
                  <Column
                    sortable
                    field="schoolStagesGuideId"
                    body={(row) => this.state.stagesById[row.schoolStagesGuideId] || "—"}
                    header="المرحلة الدراسية"
                  ></Column>
                  <Column
                    sortable
                    field="periodId"
                    body={(row) => this.state.periodsById[row.periodId] || "—"}
                    header="الفترة"
                  ></Column>
                  <Column
                    body={contentBody}
                    header="المحتوى"
                  ></Column>
                  <Column
                    sortable
                    field="lessonType"
                    body={typeBody}
                    header="نوع الدرس"
                  ></Column>
                  <Column
                    header="تعديل / حذف"
                    body={actionBodyTemplate}
                  ></Column>
                </DataTable>
              </Card>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default AllLessons;
